import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AdminRideBookingDto,
  AdminRideDetailsDto,
  AdminRideSortBy,
  AdminRideStatus,
  AdminRideSummaryDto,
  AdminNotificationCommunityOption,
  adminCancelRideBooking,
  adminRideAction,
  getAdminRide,
  getAdminRides,
  getAdminNotificationCommunities,
  PageDto,
  useApiClient,
} from '../api/client';
import { RideOperationsTabs } from '../components/RideOperationsTabs';
import { AdminRideRouteMap } from '../components/AdminRideRouteMap';
import { UserTypeahead } from '../components/UserTypeahead';
import { EmptyRow, FilterBar, InfoTile, PageHeader, PageShell, PaginationBar, StatusBadge, toneForPaymentStatus, toneForRideStatus } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';
import { useToast } from '../toast/ToastProvider';
import { formatDateTime } from '../utils/dateFormat';
import { readRideFilters, rideQuery, rideStatuses, rideTypes, ridePeriods } from '../utils/rideFilters';
import { viennaDay } from '../utils/bookingFilters';

const SORT_OPTIONS: AdminRideSortBy[] = ['time', 'createdAt'];

function formatName(user?: { email?: string | null; firstName?: string | null; lastName?: string | null } | null) {
  if (!user) return 'Unknown user';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || 'Unknown user';
}

function formatMoney(cents?: number | null) {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
}

function formatMaybeDate(value?: string | null) {
  return value ? formatDateTime(value) : '—';
}

function copyText(value?: string | null) {
  if (!value) return;
  void navigator.clipboard?.writeText(value);
}

function rideActionState(ride: AdminRideDetailsDto | AdminRideSummaryDto, action: 'start' | 'cancel' | 'complete') {
  if (action === 'start') {
    if (ride.startedAt) return 'Already started';
    if (ride.completedAt) return 'Already completed';
    if (ride.cancelledAt || ride.expiredAt) return 'Ride is inactive';
    return null;
  }
  if (action === 'cancel') {
    if (ride.completedAt) return 'Completed rides cannot be cancelled';
    if (ride.cancelledAt) return 'Already cancelled';
    if (ride.expiredAt) return 'Expired rides cannot be cancelled';
    if (ride.startedAt && ride.activePassengerCount > 0) return 'Started ride has active passengers';
    return null;
  }
  if (ride.completedAt) return 'Already completed';
  if (ride.cancelledAt || ride.expiredAt) return 'Ride is inactive';
  return null;
}

export const RidesListPage: React.FC = () => {
  const { t } = useI18n();
  const { request } = useApiClient();
  const { showError, showSuccess, showInfo } = useToast();
  const [search, setSearch] = useSearchParams();
  const key = search.toString();
  const filters = readRideFilters(search);
  const { q, status, sortBy, rideType, userId, userName, userRole, communityId, period, start, end, sortDirection } = filters;
  const pageNumber = filters.page;
  const pageSize = filters.size;
  const today = viennaDay(new Date());
  let invalidRange = false;
  try { rideQuery(search, new Date()); } catch { invalidRange = true; }
  const [page, setPage] = React.useState<PageDto<AdminRideSummaryDto> | null>(null);
  const [details, setDetails] = React.useState<Record<string, AdminRideDetailsDto>>({});
  const [expandedRideId, setExpandedRideId] = React.useState<string | null>(null);
  const [communities, setCommunities] = React.useState<AdminNotificationCommunityOption[]>([]);
  const [communitiesError, setCommunitiesError] = React.useState(false);
  const [communityReload, setCommunityReload] = React.useState(0);
  const [ongoingRideCount, setOngoingRideCount] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadingDetailsId, setLoadingDetailsId] = React.useState<string | null>(null);
  const [actionKey, setActionKey] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const generation = React.useRef(0);
  const invalidateRequests = React.useCallback(() => { ++generation.current; }, []);

  React.useEffect(() => {
    let active = true;
    setCommunitiesError(false);
    getAdminNotificationCommunities(request)
      .then((result) => { if (active) setCommunities(result); })
      .catch(() => { if (active) setCommunitiesError(true); });
    return () => { active = false; };
  }, [request, communityReload]);

  function update(values: Record<string, string>, resetPage = true) {
    invalidateRequests();
    const next = new URLSearchParams(search);
    Object.entries(values).forEach(([name, value]) => { if (value) next.set(name, value); else next.delete(name); });
    if (resetPage) next.set('page', '0');
    setSearch(next, { replace: Object.keys(values).length === 1 && 'q' in values });
  }

  const load = React.useCallback(async () => {
    const ticket = ++generation.current;
    let query;
    try { query = rideQuery(new URLSearchParams(key), new Date()); }
    catch { setLoading(false); setError(null); return; }
    setLoading(true);
    setError(null);
    setOngoingRideCount(null);
    try {
      const [data, ongoingData] = await Promise.all([
        getAdminRides(request, query),
        getAdminRides(request, { ...query, page: 0, size: 1, status: 'STARTED' }),
      ]);
      if (ticket !== generation.current) return;
      setPage(data);
      setOngoingRideCount(ongoingData.totalElements);
      if (data.totalPages > 0 && query.page >= data.totalPages) {
        setSearch((current) => {
          if (current.toString() !== key) return current;
          const next = new URLSearchParams(current);
          next.set('page', String(data.totalPages - 1));
          return next;
        }, { replace: true });
      }
    } catch (err) {
      if (ticket !== generation.current) return;
      const message = (err as Error)?.message || 'Failed to load rides';
      setError(message);
      showError(message);
    } finally {
      if (ticket === generation.current) setLoading(false);
    }
  }, [key, request, setSearch, showError]);

  const latestLoad = React.useRef(load);
  latestLoad.current = load;

  React.useEffect(() => {
    const timeout = window.setTimeout(load, 250);
    return () => { window.clearTimeout(timeout); invalidateRequests(); };
  }, [load, invalidateRequests]);

  async function expandRide(rideId: string) {
    if (expandedRideId === rideId) {
      setExpandedRideId(null);
      return;
    }
    setExpandedRideId(rideId);
    if (details[rideId]) return;
    setLoadingDetailsId(rideId);
    try {
      const data = await getAdminRide(request, rideId);
      setDetails((prev) => ({ ...prev, [rideId]: data }));
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to load ride details';
      showError(message);
    } finally {
      setLoadingDetailsId(null);
    }
  }

  async function runRideAction(rideId: string, action: 'start' | 'cancel' | 'complete') {
    const label = action === 'start' ? 'start this ride' : action === 'cancel' ? 'cancel this ride' : 'complete this ride';
    if (!window.confirm(`Admin action: ${label}?`)) return;
    setActionKey(`${rideId}:${action}`);
    try {
      const updated = await adminRideAction(request, rideId, action);
      setDetails((prev) => ({ ...prev, [rideId]: updated }));
      showSuccess(`Ride ${action} action completed.`);
      await latestLoad.current();
    } catch (err) {
      const message = (err as Error)?.message || `Failed to ${action} ride`;
      showError(message);
    } finally {
      setActionKey(null);
    }
  }

  async function cancelBooking(rideId: string, bookingId: string) {
    if (!window.confirm(`Admin action: cancel booking ${bookingId}?`)) return;
    setActionKey(`${rideId}:booking:${bookingId}`);
    try {
      const updated = await adminCancelRideBooking(request, rideId, bookingId);
      setDetails((prev) => ({ ...prev, [rideId]: updated }));
      showSuccess('Booking cancelled.');
      await latestLoad.current();
    } catch (err) {
      const message = (err as Error)?.message || 'Failed to cancel booking';
      showError(message);
    } finally {
      setActionKey(null);
    }
  }

  function resetFilters() {
    invalidateRequests();
    setSearch(pageSize === 10 ? {} : { size: String(pageSize) });
    showInfo(t('rides.filtersCleared'));
  }

  const rows = invalidRange ? [] : page?.content || [];
  const statusLabel = React.useCallback((value: '' | AdminRideStatus) => {
    if (!value) return t('rides.allStatuses');
    return t(`rides.status.${value.toLowerCase()}`);
  }, [t]);
  const sortLabel = React.useCallback((value: AdminRideSortBy) => t(`rides.sort.${value}`), [t]);
  const activeFilters: Array<{ label: string; value: string; clear: Record<string, string> }> = [];
  if (q) activeFilters.push({ label: t('rides.filters.search'), value: q, clear: { q: '' } });
  if (communityId) activeFilters.push({ label: t('rides.filters.community'), value: communities.find((community) => community.id === communityId)?.name || communityId, clear: { communityId: '' } });
  if (userId) activeFilters.push({ label: t('rides.filters.user'), value: userName, clear: { userId: '', userName: '', userRole: '' } });
  if (userRole) activeFilters.push({ label: t('rides.filters.role'), value: t(`rides.filters.role.${userRole}`), clear: { userRole: '' } });
  if (rideType) activeFilters.push({ label: t('rides.filters.type'), value: t(`rides.filters.type.${rideType}`), clear: { rideType: '' } });
  if (status) activeFilters.push({ label: t('rides.filters.status'), value: statusLabel(status), clear: { status: '' } });
  if (period !== 'all') activeFilters.push({ label: t('rides.filters.period'), value: period === 'range' ? `${start} – ${end}` : t(`rides.filters.period.${period}`), clear: { period: '', start: '', end: '' } });
  if (filters.eventId) activeFilters.push({ label: 'Event', value: filters.eventId, clear: { eventId: '' } });

  return (
    <PageShell>
      <RideOperationsTabs />
        <PageHeader
          title={t('rides.title')}
          description={t('rides.description')}
          meta={
            <div className="admin-ride-ongoing" aria-live="polite">
              <span className={`admin-ride-ongoing-dot${ongoingRideCount ? ' is-active' : ''}`} />
              {ongoingRideCount == null ? t('rides.ongoing.loading') : t(ongoingRideCount === 1 ? 'rides.ongoing.count.one' : 'rides.ongoing.count', { count: ongoingRideCount })}
            </div>
          }
          actions={
            <>
              <button className="btn btn-neutral" onClick={load} disabled={loading || invalidRange}>{t('action.refresh')}</button>
            </>
          }
        />

        <FilterBar className="admin-rides-filters">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_170px]">
            <label className="admin-filter-field"><span>{t('rides.filters.search')}</span><input className="form-control w-full" type="search" placeholder={t('rides.searchPlaceholder')} value={q} onChange={(event) => update({ q: event.target.value })} /></label>
            <label className="admin-filter-field"><span>{t('rides.filters.community')}</span>
              <select className="form-control w-full" value={communityId} onChange={(event) => update({ communityId: event.target.value, userId: '', userName: '', userRole: '' })}>
                <option value="">{t('rides.filters.allCommunities')}</option>
                {communityId && !communities.some((community) => community.id === communityId) && <option value={communityId}>{communityId}</option>}
                {communities.map((community) => <option key={community.id} value={community.id}>{community.name}</option>)}
              </select>
            </label>
            <div className="admin-filter-field"><span>{t('rides.filters.user')}</span>
              {userId ? (
                <div className="form-control flex min-w-0 items-center justify-between gap-2">
                  <span className="truncate">{userName}</span>
                  <button type="button" className="text-xs font-semibold text-slate-500" aria-label={t('rides.filters.remove', { label: t('rides.filters.user') })} onClick={() => update({ userId: '', userName: '', userRole: '' })}>×</button>
                </div>
              ) : <UserTypeahead key={communityId} ariaLabel={t('rides.filters.user')} communityId={communityId || undefined} placeholder={t('rides.filterByUser')} onSelect={(user) => update({ userId: user.id, userName: formatName(user) })} />}
            </div>
            <label className="admin-filter-field"><span>{t('rides.filters.role')}</span><select className="form-control w-full" value={userRole} disabled={!userId} onChange={(event) => update({ userRole: event.target.value })}>
              <option value="">{t('rides.filters.role.all')}</option>{['DRIVER', 'RIDER'].map((role) => <option key={role} value={role}>{t(`rides.filters.role.${role}`)}</option>)}
            </select></label>
          </div>
          {communityId && <p className="text-xs text-slate-500">{t('rides.filters.communityHint')}</p>}
          {communitiesError && <div className="flex items-center gap-3 text-xs text-red-700" role="alert">{t('rides.filters.communityError')}<button type="button" className="btn btn-neutral" onClick={() => setCommunityReload((value) => value + 1)}>{t('action.refresh')}</button></div>}
          <div className="admin-rides-filter-quick">
            <div className="admin-filter-field"><span>{t('rides.filters.type')}</span><div className="admin-segmented-control" role="group" aria-label={t('rides.filters.type')}>
              {rideTypes.map((type) => <button key={type || 'all'} type="button" aria-pressed={rideType === type} onClick={() => update({ rideType: type })}>{t(`rides.filters.type.${type || 'all'}`)}</button>)}
            </div></div>
            <label className="admin-filter-field"><span>{t('rides.filters.period')}</span><select className="form-control" value={period} onChange={(event) => update({ period: event.target.value, start: event.target.value === 'range' ? start || today : '', end: event.target.value === 'range' ? end || today : '', sortDirection: '' })}>
              {ridePeriods.map((value) => <option key={value} value={value}>{t(`rides.filters.period.${value}`)}</option>)}
            </select></label>
            {period === 'range' && <>
              <label className="admin-filter-field"><span>{t('rides.filters.from')}</span><input className="form-control" type="date" value={start} onChange={(event) => update({ start: event.target.value })} /></label>
              <label className="admin-filter-field"><span>{t('rides.filters.to')}</span><input className="form-control" type="date" value={end} onChange={(event) => update({ end: event.target.value })} /></label>
            </>}
          </div>
          {period !== 'all' && <p className="text-xs text-slate-500">{t('rides.filters.zone')}</p>}
          <div className="grid items-end gap-3 border-t border-slate-200 pt-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_100px_auto]">
            <label className="admin-filter-field"><span>{t('rides.filters.status')}</span><select className="form-control w-full" value={status} onChange={(event) => update({ status: event.target.value })}>{rideStatuses.map((value) => <option key={value || 'all'} value={value}>{statusLabel(value)}</option>)}</select></label>
            <label className="admin-filter-field"><span>{t('rides.sort.label')}</span><select className="form-control w-full" value={sortBy} onChange={(event) => update({ sortBy: event.target.value })}>{SORT_OPTIONS.map((value) => <option key={value} value={value}>{sortLabel(value)}</option>)}</select></label>
            <label className="admin-filter-field"><span>{t('rides.filters.direction')}</span><select className="form-control w-full" value={sortDirection} onChange={(event) => update({ sortDirection: event.target.value })}>{['ASC', 'DESC'].map((value) => <option key={value} value={value}>{t(`rides.filters.direction.${value}`)}</option>)}</select></label>
            <label className="admin-filter-field"><span>{t('rides.filters.size')}</span><select className="form-control w-full" value={pageSize} onChange={(event) => update({ size: event.target.value })}>{[10,20,50,100].map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
            <button className="btn btn-neutral" onClick={resetFilters}>{t('action.clear')}</button>
          </div>
          {!!activeFilters.length && <div className="admin-rides-filter-chips" aria-label={t('rides.filters.active')}>
            {activeFilters.map((filter) => <button type="button" key={filter.label} className="chip" aria-label={t('rides.filters.remove', { label: filter.label })} onClick={() => update(filter.clear)}><span>{filter.label}: {filter.value}</span><span aria-hidden="true">×</span></button>)}
          </div>}
        </FilterBar>

        {(invalidRange || error) && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{invalidRange ? t('rides.filters.invalidRange') : error}</div>}

        <div className="admin-panel admin-rides-table-card">
          <div className="admin-rides-table-meta">
            <span>{loading ? t('rides.loading') : t('rides.found', { count: page?.totalElements || 0 })}</span>
          </div>
          <div className="admin-table-scroll" tabIndex={0} aria-label={t('rides.table.scrollLabel')}>
            <table className="admin-table admin-rides-table">
              <thead>
                <tr>
                  <th>{t('rides.table.departureRoute')}</th>
                  <th>{t('rides.table.driver')}</th>
                  <th>{t('rides.table.bookings')}</th>
                  <th>{t('rides.table.status')}</th>
                  <th>{t('rides.table.payments')}</th>
                  <th><span className="sr-only">{t('rides.table.actions')}</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((ride) => {
                  const expanded = expandedRideId === ride.id;
                  const detail = details[ride.id];
                  const expandLabel = t(expanded ? 'rides.action.collapseInline' : 'rides.action.expandInline');
                  return (
                    <React.Fragment key={ride.id}>
                      <tr className="admin-ride-row">
                        <td className={`admin-ride-route-cell ${ride.recurring ? 'is-recurring' : 'is-single'}`}>
                          <Link className="admin-ride-departure" to={`/rides/${ride.id}`}>
                            {formatDateTime(ride.time)}
                          </Link>
                          <div className="admin-ride-route">
                            <span>{ride.departure.publicName}</span>
                            <span aria-hidden="true">→</span>
                            <span>{ride.destination.publicName}</span>
                          </div>
                          <div className="admin-ride-type">
                            <StatusBadge tone={ride.recurring ? 'violet' : 'slate'}>
                              {t(`rides.type.${ride.recurring ? 'recurring' : 'single'}`)}
                            </StatusBadge>
                            {ride.recurringSeriesName && <span className="admin-ride-series-label">{ride.recurringSeriesName}</span>}
                          </div>
                        </td>
                        <td className="admin-ride-driver-cell">
                          <div className="admin-ride-driver-name">{formatName(ride.driver)}</div>
                          <button className="admin-ride-driver-contact" onClick={() => copyText(ride.driver.id)} title={ride.driver.email || ride.driver.id}>
                            {ride.driver.email || ride.driver.id}
                          </button>
                        </td>
                        <td className="admin-ride-bookings-cell">
                          <div className="admin-ride-booking-count">{t('rides.bookingsCount', { count: ride.bookingCount })}</div>
                          <div className="admin-ride-booking-seats">{t('rides.seatsApproved', { approved: ride.approvedSeats, total: ride.numberOfSeats })}</div>
                        </td>
                        <td>
                          <StatusBadge tone={toneForRideStatus(ride.status)}>{statusLabel(ride.status)}</StatusBadge>
                        </td>
                        <td className="admin-ride-payment-cell">
                          <div className="admin-ride-earnings">{formatMoney(ride.totalDriverEarningsInCents)}</div>
                          <div className="admin-ride-payment-state">
                            {ride.paymentIssueCount > 0
                              ? <StatusBadge tone="red">{t('rides.paymentIssues', { count: ride.paymentIssueCount })}</StatusBadge>
                              : <span>{t('rides.noPaymentIssues')}</span>}
                          </div>
                        </td>
                        <td className="admin-ride-actions-cell">
                          <div>
                            <Link className="btn btn-primary admin-ride-open" to={`/rides/${ride.id}`}>
                              {t('rides.action.openDetails')}
                            </Link>
                            <button
                              type="button"
                              className="admin-ride-expand"
                              aria-label={expandLabel}
                              aria-expanded={expanded}
                              aria-controls={`ride-details-${ride.id}`}
                              title={expandLabel}
                              onClick={() => expandRide(ride.id)}
                            >
                              <svg aria-hidden="true" viewBox="0 0 20 20" fill="none">
                                <path d={expanded ? 'm5 12 5-5 5 5' : 'm5 8 5 5 5-5'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr id={`ride-details-${ride.id}`} hidden={!expanded} className="admin-ride-expanded-row">
                        <td colSpan={6}>
                          {expanded && loadingDetailsId === ride.id && <div className="text-sm text-gray-500">{t('rides.details.loading')}</div>}
                          {expanded && detail && (
                            <RideDetailsPanel
                              ride={detail}
                              actionKey={actionKey}
                              onRideAction={runRideAction}
                              onCancelBooking={cancelBooking}
                            />
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {!loading && rows.length === 0 && (
                  <EmptyRow colSpan={6}>{t('rides.noMatches')}</EmptyRow>
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar
            loading={loading || invalidRange}
            pageNumber={pageNumber}
            pageSize={pageSize}
            rowCount={rows.length}
            displayPage={page ? { number: page.number, size: page.size } : undefined}
            totalElements={page?.totalElements}
            isLast={page?.last}
            onPrevious={() => update({ page: String(Math.max(0, pageNumber - 1)) }, false)}
            onNext={() => update({ page: String(pageNumber + 1) }, false)}
            previousLabel={t('action.previous')}
            nextLabel={t('action.next')}
          />
        </div>
    </PageShell>
  );
};

function RideDetailsPanel({
  ride,
  actionKey,
  onRideAction,
  onCancelBooking,
}: {
  ride: AdminRideDetailsDto;
  actionKey: string | null;
  onRideAction: (rideId: string, action: 'start' | 'cancel' | 'complete') => void;
  onCancelBooking: (rideId: string, bookingId: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="grid gap-4">
      <div className="admin-ride-detail-id">
        <span>{t('rides.details.id')}</span>
        <button type="button" onClick={() => copyText(ride.id)} title={t('rides.details.copyId')}>{ride.id}</button>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <Info label="Created" value={formatMaybeDate(ride.createdAt)} />
        <Info label="Type" value={ride.recurring ? `Recurring${ride.recurringSeriesName ? `: ${ride.recurringSeriesName}` : ''}` : 'Single'} />
        <Info label="Updated" value={formatMaybeDate(ride.updatedAt)} />
        <Info label="Started" value={formatMaybeDate(ride.startedAt)} />
        <Info label="Completed" value={formatMaybeDate(ride.completedAt)} />
        <Info label="Cancelled" value={formatMaybeDate(ride.cancelledAt)} />
        <Info label="Expired" value={formatMaybeDate(ride.expiredAt)} />
        <Info label="Distance" value={ride.distanceMeters ? `${(ride.distanceMeters / 1000).toFixed(1)} km` : '—'} />
        <Info label="Revenue" value={`${formatMoney(ride.totalPriceInCents)} total · ${formatMoney(ride.totalServiceFeeInCents)} fee`} />
      </div>

      <div className="flex flex-wrap gap-2">
        {(['start', 'cancel', 'complete'] as const).map((action) => {
          const disabledReason = rideActionState(ride, action);
          const busy = actionKey === `${ride.id}:${action}`;
          return (
            <div key={action} className="flex items-center gap-2">
              <button
                className={`btn ${action === 'cancel' ? 'btn-danger' : action === 'complete' ? 'btn-success' : 'btn-primary'}`}
                disabled={!!disabledReason || busy}
                onClick={() => onRideAction(ride.id, action)}
              >
                {busy ? 'Working…' : action[0].toUpperCase() + action.slice(1)}
              </button>
              {disabledReason && <span className="text-xs text-gray-500">{disabledReason}</span>}
            </div>
          );
        })}
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-gray-900">Ride route map</div>
        <AdminRideRouteMap ride={ride} />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b px-4 py-3 font-semibold text-gray-900">Bookings and ride requests</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Booking</th>
                <th className="px-3 py-2 font-semibold">Rider</th>
                <th className="px-3 py-2 font-semibold">Request</th>
                <th className="px-3 py-2 font-semibold">Pickup / Dropoff</th>
                <th className="px-3 py-2 font-semibold">Lifecycle</th>
                <th className="px-3 py-2 font-semibold">Payment</th>
                <th className="px-3 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {ride.bookings.map((booking) => (
                <BookingRow
                  key={booking.id}
                  rideId={ride.id}
                  booking={booking}
                  actionKey={actionKey}
                  onCancelBooking={onCancelBooking}
                />
              ))}
              {ride.bookings.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-4 text-center text-gray-500">No bookings for this ride.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function BookingRow({
  rideId,
  booking,
  actionKey,
  onCancelBooking,
}: {
  rideId: string;
  booking: AdminRideBookingDto;
  actionKey: string | null;
  onCancelBooking: (rideId: string, bookingId: string) => void;
}) {
  const { t } = useI18n();
  const cancelDisabledReason = booking.cancelledAt
    ? 'Already cancelled'
    : booking.pickedUpAt
      ? 'Cannot cancel after pickup'
      : booking.completedAt
        ? 'Already completed'
        : null;
  const busy = actionKey === `${rideId}:booking:${booking.id}`;
  return (
    <tr className="border-t align-top">
      <td className="px-3 py-2">
        <button className="max-w-[140px] truncate font-semibold text-gray-900 hover:text-gray-600" onClick={() => copyText(booking.id)} title="Copy booking ID">{booking.id}</button>
        <Link className="mt-1 block text-xs text-gray-900 hover:underline" to={`/rides/bookings/${encodeURIComponent(booking.id)}`}>{t('bookings.lifecycle')}</Link>
      </td>
      <td className="px-3 py-2">
        <div className="font-semibold text-gray-900">{formatName(booking.rider)}</div>
        <button className="text-gray-500 hover:text-gray-900" onClick={() => copyText(booking.rider?.id)}>{booking.rider?.email || booking.rider?.id || '—'}</button>
      </td>
      <td className="px-3 py-2">
        <button className="max-w-[140px] truncate text-gray-700 hover:text-gray-900" onClick={() => copyText(booking.rideRequestId)} title="Copy ride request ID">{booking.rideRequestId || '—'}</button>
        <div className="text-gray-500">{booking.rideRequestStatus || '—'} · {booking.seatsRequested || 1} seat(s)</div>
      </td>
      <td className="px-3 py-2">
        <div>{booking.pickup?.publicName || '—'}</div>
        <div className="text-gray-500">to {booking.dropoff?.publicName || '—'}</div>
      </td>
      <td className="px-3 py-2 text-gray-600">
        <div>Approved: {formatMaybeDate(booking.approvedAt)}</div>
        <div>Rejected: {formatMaybeDate(booking.rejectedAt)}</div>
        <div>Picked up: {formatMaybeDate(booking.pickedUpAt)}</div>
        <div>Dropped off: {formatMaybeDate(booking.completedAt)}</div>
        <div>Cancelled: {formatMaybeDate(booking.cancelledAt)}</div>
      </td>
      <td className="px-3 py-2">
        <StatusBadge tone={toneForPaymentStatus(booking.paymentCaptureStatus)}>{booking.paymentCaptureStatus || '—'}</StatusBadge>
        <div className="text-gray-500">{formatMoney(booking.priceInCents)} · fee {formatMoney(booking.serviceFeeInCents)}</div>
        <button className="max-w-[160px] truncate text-gray-500 hover:text-gray-900" onClick={() => copyText(booking.stripePaymentIntentId)} title="Copy payment intent">
          {booking.stripePaymentIntentId || 'No payment intent'}
        </button>
        {booking.paymentCaptureLastError && <div className="mt-1 max-w-[220px] text-red-700">{booking.paymentCaptureLastError}</div>}
      </td>
      <td className="px-3 py-2 text-right">
        <button
          className="btn btn-danger"
          disabled={!!cancelDisabledReason || busy}
          onClick={() => onCancelBooking(rideId, booking.id)}
        >
          {busy ? 'Working…' : 'Cancel'}
        </button>
        {cancelDisabledReason && <div className="mt-1 max-w-[120px] text-right text-gray-500">{cancelDisabledReason}</div>}
      </td>
    </tr>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <InfoTile label={label} value={value} />;
}

export default RidesListPage;
